import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { WebView, type WebViewNavigation } from 'react-native-webview';
import { ScreenHeader } from '../components/ScreenHeader';
import { useProfile } from '../hooks/useProfile';
import { getAuthEmail } from '../services/profile';
import { calBookingUrl, findFormule, type CoachingFormuleId } from '../config/coaching';
import { colors, radius, screenPadding, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';
import { useTarifsCoaching } from '../hooks/useTarifsCoaching';
import { libellePrix } from '../services/tarifsCoaching';

export interface CoachingBookingScreenProps {
  formule: CoachingFormuleId;
  onBack?: () => void;
  /**
   * Appelé quand Cal.com a confirmé la réservation. `bookingUid` identifie le rendez-vous :
   * il est repris tel quel au paiement, puis recoupé avec le webhook Cal.com côté serveur.
   */
  onBooked?: (bookingUid: string) => void;
}

/**
 * Extrait l'identifiant de réservation d'une URL Cal.com de confirmation.
 * Après validation, le Booker navigue vers `https://cal.com/booking/<uid>?...`.
 * Renvoie null pour toute autre URL : on ne veut surtout pas déclencher le paiement à tort.
 */
export function extractBookingUid(url: string): string | null {
  const match = /^https?:\/\/(?:[\w-]+\.)?cal\.com\/booking\/([\w-]+)/.exec(url);
  return match ? match[1] : null;
}

export function CoachingBookingScreen({ formule, onBack, onBooked }: CoachingBookingScreenProps) {
  const { etat } = useTarifsCoaching();
  const { profile } = useProfile();
  const [loading, setLoading] = useState(true);
  const [booked, setBooked] = useState(false);
  const f = findFormule(formule);

  // L'URL ne doit être calculée qu'une fois : la recalculer rechargerait la WebView et
  // ferait perdre à l'utilisateur le créneau qu'il est en train de choisir.
  const [prefill] = useState(() => ({ name: '', email: '' }));
  const url = useMemo(() => {
    if (!f) return null;
    return calBookingUrl(f, prefill);
  }, [f, prefill]);

  // Le profil arrive de façon asynchrone : on ne remplit qu'avant le premier rendu utile.
  useEffect(() => {
    if (!profile || prefill.email) return;
    const full = [profile.first_name, profile.last_name].map((v) => v?.trim()).filter(Boolean).join(' ');
    prefill.name = full || profile.full_name?.trim() || '';
    prefill.email = profile.email || getAuthEmail() || '';
  }, [profile, prefill]);

  const onNavigationStateChange = (nav: WebViewNavigation) => {
    if (booked) return;
    const uid = extractBookingUid(nav.url);
    if (!uid) return;
    setBooked(true);
    onBooked?.(uid);
  };

  if (!f || !url) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <ScreenHeader onBack={onBack} title={t('coaching.pickSlot')} testID="coaching-booking-back" />
        <View style={styles.center}>
          <Text style={styles.error}>{t('coaching.formuleNotFound')}</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader onBack={onBack} title={t('coaching.pickSlot')} testID="coaching-booking-back" />

      <View style={styles.recap} testID="coaching-booking-recap">
        <Text style={styles.recapTitle}>{f.title}</Text>
        {/* Même règle que partout : pas de tarif lu, pas de prix affiché. */}
        {libellePrix(etat, f.id) ? (
          <Text testID="coaching-booking-prix" style={styles.recapPrice}>
            {libellePrix(etat, f.id)}
          </Text>
        ) : null}
      </View>
      <Text style={styles.hint}>
        Choisis ton créneau ci-dessous. Le paiement n’intervient qu’une fois le rendez-vous confirmé.
      </Text>

      <View style={styles.webWrap}>
        <WebView
          testID="coaching-booking-webview"
          source={{ uri: url }}
          onLoadEnd={() => setLoading(false)}
          onNavigationStateChange={onNavigationStateChange}
          startInLoadingState={false}
          javaScriptEnabled
          domStorageEnabled
          // Le Booker ouvre parfois la confirmation dans une nouvelle fenêtre : on la garde ici,
          // sinon on perdrait l'URL de confirmation et donc la détection de la réservation.
          setSupportMultipleWindows={false}
          style={styles.web}
        />
        {loading ? (
          <View style={styles.loader} testID="coaching-booking-loader">
            <ActivityIndicator color={colors.electric} />
          </View>
        ) : null}
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: screenPadding },
  error: { fontSize: typography.body.size, color: colors.error, textAlign: 'center' },

  recap: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginHorizontal: screenPadding,
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: radius.lg,
    backgroundColor: colors.white,
  },
  recapTitle: { fontSize: typography.h3.size, fontWeight: '700', color: colors.night },
  recapPrice: { fontSize: typography.h3.size, fontWeight: '800', color: colors.violet },

  hint: {
    marginHorizontal: screenPadding,
    marginTop: spacing.sm,
    marginBottom: spacing.md,
    fontSize: typography.small.size,
    lineHeight: 18,
    color: colors.grey500,
  },

  webWrap: { flex: 1, overflow: 'hidden' },
  web: { flex: 1, backgroundColor: colors.white },
  loader: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.white },
});
